import { getDefaultForm } from '@/_legacy/store/checklist_builder';
import { InspectionItemType } from '@/entities/inspection/constants';
import { computed, ref } from 'vue';
import { AiChecklistTemplate, AiPromptIdea } from '@/entities/aiChecklist';
import { useTask } from '@/ui/concerns';
import { promptIdeas, status, textPromptAi, uploadAi } from '@/services/api/resources/aiChecklist';
import { pushRoute, scrollToTop, toasted } from '@/ui/helpers';
import { t } from '@/ui/i18n/i18n';
import { v4 as uuidv4 } from 'uuid';
import { CLICK_EVENTS, trackClick } from '@/services/activityTracker/activityTrackerHelper';
import { useChecklistSessionStorage } from '@/ui/concerns/useChecklistSessionStorage';
import {
  JobStatus,
  MaxJobTimeout,
  RateLimitKeyError,
  SingleJobStatusCheckInterval,
} from '@/entities/aiChecklist/constants';
import { useTemplate } from '@/ui/concerns/checklist/useTemplate';
import { fetchUser } from '@/ui/auth/auth';

export function useAiFileUpload() {
  const { saveForm } = useChecklistSessionStorage();
  const loading = ref(false);
  const aiPrompt = ref('');
  const aiPromptIdeas = ref<AiPromptIdea[]>([]);
  const uploadedFiles = ref<File[]>([]);
  const jobStatusCallCount = ref(0);
  const aiTemplate = ref(<AiChecklistTemplate|null>null);
  const formats = ['.PDF', '.PNG', '.JPG', '.XLS', '.XLSX'];
  const types = [
    'application/pdf',
    'image/png',
    'image/jpeg',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  ];
  const jobUuid = ref<string|null>(null);
  const maxCalls = Math.ceil(MaxJobTimeout / SingleJobStatusCheckInterval);
  const hasExceedJobCount = computed(() => jobStatusCallCount.value > maxCalls);
  const {
    newChecklistFromTemplate,
  } = useTemplate();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const normalizeForPreview = (generatedTemplate: any) => {
    const newForm = {
      ...{ form: getDefaultForm() },
      ...generatedTemplate,
    };

    return {
      ...newForm,
      pages: newForm.form.items.filter((item: { type: string }) => item.type === InspectionItemType.Page),
      entityItems: {},
      sites: {},
      users: {},
      id: 0,
    };
  };

  const scrollPreviewToTop = () => {
    const $el = document.querySelector('.frame__content > div');

    if ($el) {
      scrollToTop($el);
    }
  };

  const clearPreview = () => {
    uploadedFiles.value = [];
    aiTemplate.value = null;
  };

  const checkJobStatus = useTask(
    () => {
      loading.value = true;
      jobStatusCallCount.value++;

      return status(jobUuid.value!);
    },
    response => {
      if (response.data.status === JobStatus.Failed) {
        toasted.error(t('upload.errors.upload-failed'));
        loading.value = false;
        jobUuid.value = null;
        if (response.data.meta?.error === RateLimitKeyError) {
          toasted.error(t('upload.errors.rate-limit'));
        }

        return;
      }

      if (response.data.status === JobStatus.Started) {
        if (hasExceedJobCount.value) {
          toasted.error(t('upload.errors.timeout'));

          return;
        }

        setTimeout(() => {
          checkJobStatus.run();
        }, SingleJobStatusCheckInterval);

        return;
      }

      aiTemplate.value = normalizeForPreview(response.data.content);
      saveForm(JSON.stringify(response.data.content));
      fetchUser()();

      loading.value = false;

      scrollPreviewToTop();

      toasted.success(t('upload.success'));
    },
    uuidv4(),
    e => {
      console.error('error', e);
      toasted.error(t('upload.errors.upload-failed'));
      loading.value = false;
    },
  );

  const uploadAiTask = useTask(
    () => {
      loading.value = true;

      return uploadAi(uploadedFiles.value);
    },
    response => {
      jobUuid.value = response.data.uuid;
      checkJobStatus.run();
      toasted.success(t('upload.loading'));
    },
    uuidv4(),
    e => {
      console.error('error', e);
      toasted.error(t('upload.errors.upload-failed'));
      loading.value = false;
      jobUuid.value = null;
    },
  );

  const generatePromptTask = useTask(
    () => {
      loading.value = true;

      return textPromptAi(aiPrompt.value);
    },
    response => {
      jobUuid.value = response.data.uuid;
      checkJobStatus.run();
      toasted.success(t('upload.loading'));
    },
    uuidv4(),
    e => {
      console.error('error', e);
      toasted.error(t('upload.errors.upload-failed'));
      loading.value = false;
      jobUuid.value = null;
    },
  );

  const getPromptIdeasTask = useTask(
    promptIdeas,
    response => { aiPromptIdeas.value = response.data; },
    uuidv4(),
    e => { console.error('error', e); },
  );

  const onUseThisTemplateClick = async (fromAiPrompt = false, fromFileUpload = false) => {
    trackClick({
      ...CLICK_EVENTS.CREATE_FILE_UPLOAD_TEMPLATE,
    });

    loading.value = true;

    const templateData = {
      title: aiTemplate.value!.checklist_title || '',
      checklist_description: aiTemplate.value!.checklist_description || '',
      form: aiTemplate.value!.form || {},
      fromAiPrompt,
      fromFileUpload,
    };

    newChecklistFromTemplate(templateData, () => {
      loading.value = false;

      toasted.success(t('checklist-templates.checklist-saved'));
      pushRoute({ name: 'forms' });
    }, () => {
      loading.value = false;
    });
  };

  const onCustomizeTemplateClick = (fromPrompt = false, fromFileUpload = false) => {
    trackClick({
      ...CLICK_EVENTS.CUSTOMIZE_FILE_UPLOAD_TEMPLATE,
    });

    pushRoute({
      name: 'checklistCreate',
      query: {
        fromAi: 'true',
        fromPrompt: fromPrompt ? 'true' : 'false',
        fromFileUpload: fromFileUpload ? 'true' : 'false',
      },
    });
  };

  const onGenerateTemplateClick = () => {
    generatePromptTask.run();
  };

  return {
    normalizeForPreview,
    clearPreview,
    aiPrompt,
    aiPromptIdeas,
    loading,
    uploadedFiles,
    aiTemplate,
    formats,
    types,
    uploadAiTask,
    getPromptIdeasTask,
    onCustomizeTemplateClick,
    onUseThisTemplateClick,
    onGenerateTemplateClick,
  };
}
